// MARK://dhk8/pos/lib/clients/printing
// Typed HTTP client for dhk8-pos-printing, reconciled against the published contract
// at services/dhk8-pos-printing/src/contract.ts (commit 24e0a3b).
//
// Model: templates are free-form markup `body` strings with `{{path}}` placeholders,
// keyed by `kind` (KITCHEN|RECEIPT|PRECUENTA|CASHCUT|LABEL). A print *job* is created
// with {kind, templateId?, payload}; the service renders the template and leaves the
// job QUEUED with `rendered` (preview). The Pi edge consumes QUEUED jobs per branch,
// drives the ESC/POS printer, and reports back via PATCH /jobs/:id/status. Physical
// printers are not a backend entity here — the Edge owns them (see PI-EDGE-REQUIREMENT).
//
// Routes: `${origin}/v1/print/...`. Envelope: { ok, data } | bare T.

import { gqlClient } from '../gql.js'
import {
  PosPrintStationsQuery, PosPrintTemplatesQuery, PosPrintJobsQuery,
  PosPrintTemplateQuery, PosPrintJobByIdQuery,
  PosCreatePrintTemplateMutation, PosUpdatePrintTemplateMutation, PosDeletePrintTemplateMutation,
  PosEnqueuePrintJobMutation, PosReprintPrintJobMutation, PosUpdatePrintJobStatusMutation,
} from '../queries/printing.queries.js'

// ── DTOs (mirror of services/dhk8-pos-printing/src/contract.ts) ─────────────────

export type TemplateKind = 'KITCHEN' | 'RECEIPT' | 'PRECUENTA' | 'CASHCUT' | 'LABEL'
export type PrintJobStatus = 'QUEUED' | 'SENT' | 'ACKED' | 'FAILED' | 'CANCELLED'

export interface PrintTemplateDto {
  id: string
  tenantId: string
  branchId: string | null
  kind: string // TemplateKind, but the backend types it loosely
  name: string
  /** Markup with `{{path}}` placeholders rendered against a job payload. */
  body: string
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface PrintJobDto {
  id: string
  tenantId: string
  branchId: string
  terminalId: string | null
  printerId: string | null
  templateId: string | null
  kind: string // TemplateKind
  payload: unknown
  /** Template rendered against `payload` — what will be printed; use for preview. */
  rendered: string | null
  status: string // PrintJobStatus
  attempts: number
  lastError: string | null
  createdAt: string
  updatedAt: string
}

interface Paged<T> { items: T[]; total: number; page: number; limit: number }

// ── Request DTOs ────────────────────────────────────────────────────────────────

export interface ListTemplatesQuery { tenantId: string; branchId?: string; kind?: TemplateKind; page?: number; limit?: number }
export interface CreateTemplateInput { tenantId: string; branchId?: string; kind: TemplateKind; name: string; body: string }
export type UpdateTemplateInput = Partial<Pick<CreateTemplateInput, 'name' | 'body' | 'kind'>> & { active?: boolean }

export interface ListJobsQuery { tenantId: string; branchId?: string; terminalId?: string; status?: PrintJobStatus; page?: number; limit?: number }
export interface EnqueueJobInput { tenantId: string; branchId: string; terminalId?: string; printerId?: string; templateId?: string; kind: TemplateKind; payload?: unknown }
export interface ReprintJobInput { terminalId?: string; printerId?: string }
export interface UpdateJobStatusInput { status: PrintJobStatus; lastError?: string }

// ── operations: templates ───────────────────────────────────────────────────────

export async function listTemplates(query: ListTemplatesQuery, signal?: AbortSignal): Promise<Paged<PrintTemplateDto>> {
  // F4.3 / ADR-0009: lectura por el grafo (pos-printing, tenant del JWT). Escrituras REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const vars: { branchId?: string; kind?: TemplateKind; page?: number; limit?: number } = {}
  if (query.branchId) vars.branchId = query.branchId
  if (query.kind) vars.kind = query.kind
  if (query.page) vars.page = query.page
  if (query.limit) vars.limit = query.limit
  const res = await gqlClient.query(PosPrintTemplatesQuery, vars, ctx).toPromise()
  if (res.error) throw res.error
  const pr = res.data?.printTemplates
  return {
    items: (pr?.items ?? []) as unknown as PrintTemplateDto[],
    total: pr?.total ?? 0, page: pr?.page ?? (query.page ?? 1), limit: pr?.limit ?? (query.limit ?? 20),
  }
}
export async function getTemplate(id: string, signal?: AbortSignal): Promise<PrintTemplateDto> {
  // F6.2 R1 / ADR-0008: lectura singular por el grafo (printTemplate(id), tenant
  // del JWT, IDOR-guard → null si no es del tenant). Sustituye GET REST /templates/:id.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(PosPrintTemplateQuery, { id }, ctx).toPromise()
  if (res.error) throw res.error
  const t = res.data?.printTemplate
  if (!t) throw new Error(`NOT_FOUND: print template ${id}`)
  return t as unknown as PrintTemplateDto
}
export async function createTemplate(input: CreateTemplateInput): Promise<PrintTemplateDto> {
  // F8.1 / ADR-0009: escritura por el grafo. El tenant lo pone el JWT (no se envia).
  const res = await gqlClient.mutation(PosCreatePrintTemplateMutation, {
    input: { kind: input.kind, name: input.name, body: input.body, branchId: input.branchId ?? null },
  }).toPromise()
  if (res.error) throw res.error
  const t = res.data?.createPrintTemplate
  if (!t) throw new Error('createPrintTemplate: respuesta vacia')
  return t as unknown as PrintTemplateDto
}
export async function updateTemplate(id: string, input: UpdateTemplateInput): Promise<PrintTemplateDto> {
  const res = await gqlClient.mutation(PosUpdatePrintTemplateMutation, { id, input }).toPromise()
  if (res.error) throw res.error
  const t = res.data?.updatePrintTemplate
  if (!t) throw new Error('updatePrintTemplate: respuesta vacia')
  return t as unknown as PrintTemplateDto
}
export async function deleteTemplate(id: string): Promise<{ id: string }> {
  const res = await gqlClient.mutation(PosDeletePrintTemplateMutation, { id }).toPromise()
  if (res.error) throw res.error
  return { id: (res.data?.deletePrintTemplate as string | undefined) ?? id }
}

// ── operations: jobs ────────────────────────────────────────────────────────────

export async function listJobs(query: ListJobsQuery, signal?: AbortSignal): Promise<Paged<PrintJobDto>> {
  // F4.3 / ADR-0009: lectura por el grafo (pos-printing, tenant del JWT). Escrituras REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const vars: { branchId?: string; terminalId?: string; status?: PrintJobStatus; page?: number; limit?: number } = {}
  if (query.branchId) vars.branchId = query.branchId
  if (query.terminalId) vars.terminalId = query.terminalId
  if (query.status) vars.status = query.status
  if (query.page) vars.page = query.page
  if (query.limit) vars.limit = query.limit
  const res = await gqlClient.query(PosPrintJobsQuery, vars, ctx).toPromise()
  if (res.error) throw res.error
  const pr = res.data?.printJobs
  return {
    items: (pr?.items ?? []) as unknown as PrintJobDto[],
    total: pr?.total ?? 0, page: pr?.page ?? (query.page ?? 1), limit: pr?.limit ?? (query.limit ?? 50),
  }
}
export async function getJob(id: string, signal?: AbortSignal): Promise<PrintJobDto> {
  // F6.2 R1 / ADR-0008: lectura singular por el grafo (printJobById(id), tenant
  // del JWT, IDOR-guard → null si no es del tenant). `printJob` lo ocupa el
  // placeholder de print-bridge-relay. Sustituye GET REST /jobs/:id.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(PosPrintJobByIdQuery, { id }, ctx).toPromise()
  if (res.error) throw res.error
  const j = res.data?.printJobById
  if (!j) throw new Error(`NOT_FOUND: print job ${id}`)
  return j as unknown as PrintJobDto
}
export async function enqueueJob(input: EnqueueJobInput): Promise<PrintJobDto> {
  // F8.1 / ADR-0009: tenant del JWT; payload (objeto) se serializa a JSON (scalar String).
  const res = await gqlClient.mutation(PosEnqueuePrintJobMutation, {
    input: {
      branchId: input.branchId, kind: input.kind, payload: JSON.stringify(input.payload ?? {}),
      terminalId: input.terminalId ?? null, printerId: input.printerId ?? null, templateId: input.templateId ?? null,
    },
  }).toPromise()
  if (res.error) throw res.error
  const j = res.data?.enqueuePrintJob
  if (!j) throw new Error('enqueuePrintJob: respuesta vacia')
  return j as unknown as PrintJobDto
}
export async function reprintJob(id: string, input: ReprintJobInput = {}): Promise<PrintJobDto> {
  const res = await gqlClient.mutation(PosReprintPrintJobMutation, { sourceJobId: id, input }).toPromise()
  if (res.error) throw res.error
  const j = res.data?.reprintPrintJob
  if (!j) throw new Error('reprintPrintJob: respuesta vacia')
  return j as unknown as PrintJobDto
}
/** Used by the Pi edge to report job progress. Exposed here for completeness. */
export async function updateJobStatus(id: string, input: UpdateJobStatusInput): Promise<PrintJobDto> {
  const res = await gqlClient.mutation(PosUpdatePrintJobStatusMutation, { id, input }).toPromise()
  if (res.error) throw res.error
  const j = res.data?.updatePrintJobStatus
  if (!j) throw new Error('updatePrintJobStatus: respuesta vacia')
  return j as unknown as PrintJobDto
}

// ── operations: stations (routing layer) ────────────────────────────────────────

export interface PrintStationDto {
  id: string
  tenantId: string
  branchId?: string | null
  name: string
  sortOrder?: number
}

/** List print stations for the active tenant. Used by KDS to render a station filter. */
export async function listStations(_tenantId: string, signal?: AbortSignal): Promise<PrintStationDto[]> {
  // F3.4/F4.3 / ADR-0009: lectura por el grafo (printStations, tenant del JWT — el arg
  // tenantId queda ignorado por compat de firma). Escrituras de estación siguen REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(PosPrintStationsQuery, {}, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.printStations ?? []) as unknown as PrintStationDto[]
}
